import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar as CalendarIcon, List } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, addWeeks, subWeeks, startOfMonth, endOfMonth, isSameDay } from 'date-fns';

const SchedulePage = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [view, setView] = useState('week'); // 'week' or 'month'

  useEffect(() => {
    fetchTasks();
  }, [currentWeekStart]);

  const fetchTasks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(response.data.filter(t => t.scheduled_date));
    } catch (error) {
      toast.error('Failed to load tasks');
    }
  };

  const weekDays = eachDayOfInterval({
    start: currentWeekStart,
    end: endOfWeek(currentWeekStart, { weekStartsOn: 1 })
  });

  const getTasksForDate = (date) => {
    return tasks.filter(task => {
      if (!task.scheduled_date) return false;
      const taskDate = new Date(task.scheduled_date);
      return isSameDay(taskDate, date);
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      scheduled: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      in_progress: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      completed: 'bg-green-500/20 text-green-400 border-green-500/30',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400';
  };

  return (
    <AdminLayout>
      <Toaster />
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin/tasks')}
              className="text-gray-400 hover:text-white hover:bg-gray-800"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <CalendarIcon className="w-8 h-8 text-orange-500" />
                Schedule
              </h1>
              <p className="text-gray-400 mt-2">View and manage task schedules</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => navigate('/admin/tasks')} variant="outline" className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700">
              <List className="w-4 h-4 mr-2" />
              List View
            </Button>
          </div>
        </div>

        {/* Calendar Navigation */}
        <div className="flex items-center justify-between bg-gray-800 p-4 rounded-lg border border-gray-700">
          <Button onClick={() => setCurrentWeekStart(subWeeks(currentWeekStart, 1))} variant="outline" className="bg-gray-900 border-gray-700 text-white">
            Previous Week
          </Button>
          <h2 className="text-xl font-semibold text-white">
            {format(currentWeekStart, 'MMM d')} - {format(endOfWeek(currentWeekStart, { weekStartsOn: 1 }), 'MMM d, yyyy')}
          </h2>
          <Button onClick={() => setCurrentWeekStart(addWeeks(currentWeekStart, 1))} variant="outline" className="bg-gray-900 border-gray-700 text-white">
            Next Week
          </Button>
        </div>

        {/* Week Calendar View */}
        <div className="grid grid-cols-7 gap-2">
          {weekDays.map((day, idx) => {
            const dayTasks = getTasksForDate(day);
            const isToday = isSameDay(day, new Date());
            
            return (
              <div
                key={idx}
                className={`bg-gray-800 border rounded-lg p-4 min-h-[300px] ${
                  isToday ? 'border-orange-500' : 'border-gray-700'
                }`}
              >
                <div className="text-center mb-4">
                  <div className="text-xs text-gray-400 uppercase">{format(day, 'EEE')}</div>
                  <div className={`text-2xl font-bold ${isToday ? 'text-orange-500' : 'text-white'}`}>
                    {format(day, 'd')}
                  </div>
                </div>
                
                <div className="space-y-2">
                  {dayTasks.map(task => (
                    <div
                      key={task.id}
                      onClick={() => navigate(`/admin/tasks/${task.id}`)}
                      className="bg-gray-900 p-2 rounded border border-gray-700 hover:border-orange-500 cursor-pointer transition-colors"
                    >
                      <div className="text-xs font-medium text-white mb-1 truncate">
                        {task.title}
                      </div>
                      <div className="flex items-center justify-between gap-1">
                        <Badge className={`${getStatusColor(task.status)} text-[10px] px-1 py-0`}>
                          {task.status}
                        </Badge>
                        {task.scheduled_time_slot && (
                          <span className="text-[10px] text-gray-500">
                            {task.scheduled_time_slot === 'morning' ? 'AM' : task.scheduled_time_slot === 'afternoon' ? 'PM' : 'All Day'}
                          </span>
                        )}
                      </div>
                      {task.assigned_technician_name && (
                        <div className="text-[10px] text-gray-500 mt-1 truncate">
                          {task.assigned_technician_name}
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {dayTasks.length === 0 && (
                    <div className="text-xs text-gray-600 text-center pt-8">
                      No tasks scheduled
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
          <h3 className="text-sm font-semibold text-white mb-3">Status Legend</h3>
          <div className="flex flex-wrap gap-3">
            <Badge className="bg-yellow-500/20 text-yellow-400">Pending</Badge>
            <Badge className="bg-blue-500/20 text-blue-400">Scheduled</Badge>
            <Badge className="bg-purple-500/20 text-purple-400">In Progress</Badge>
            <Badge className="bg-green-500/20 text-green-400">Completed</Badge>
            <Badge className="bg-red-500/20 text-red-400">Cancelled</Badge>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default SchedulePage;
